/**
 * Shared primitives for the Office client-tool executors (Excel / Word /
 * Outlook). These are the byte-identical pieces the per-host executors used to
 * duplicate: the timeout race, batch validation, summed-timeout calc, the
 * self-syncing flush decision, and the batch summary. Each host keeps its own
 * `Host.run()` setup/teardown (Excel pins the active sheet, suspends screen
 * updating, disables events, and recovers the runtime on timeout; Word is
 * lighter; Outlook has no `Host.run`) — only the surrounding boilerplate is
 * shared here.
 *
 * @see ADR-043 — Host-Neutral Client-Side Tool Protocol
 */

/** One operation inside a `batch_operations` call. */
export interface BatchOperation {
  args: Record<string, unknown>;
  tool: string;
}

/** Per-operation result inside a batch (fail-forward: one error doesn't abort). */
export interface BatchOperationResult {
  error?: string;
  result?: unknown;
  status: "success" | "error";
  tool: string;
}

/**
 * Race a host-run promise against a timeout, always clearing the timer.
 * Office.js has no cancellation, so a timed-out run keeps going in the
 * background — callers that need to repair host state (e.g. Excel re-enabling
 * events) do so in their own `catch`.
 */
export async function raceWithTimeout<T>(
  work: Promise<T>,
  timeoutMs: number,
  message: string
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/** Summed per-op timeout for a batch, capped (default 2 min). */
export function sumToolTimeouts(
  operations: readonly BatchOperation[],
  toolTimeouts: Record<string, number>,
  defaultTimeout: number,
  cap = 120_000
): number {
  return Math.min(
    operations.reduce(
      (sum, op) => sum + (toolTimeouts[op.tool] ?? defaultTimeout),
      0
    ),
    cap
  );
}

/**
 * Validate a batch's operations against a host's registry: no nesting, all
 * tools known. Throws a host-labelled error (e.g. "Unknown Excel tool…").
 */
export function assertBatchTools(
  operations: readonly BatchOperation[],
  coreRegistry: Record<string, unknown>,
  hostLabel: string
): void {
  for (const op of operations) {
    if (op.tool === "batch_operations") {
      throw new Error("Cannot nest batch_operations inside batch_operations.");
    }
    if (!coreRegistry[op.tool]) {
      throw new Error(`Unknown ${hostLabel} tool in batch: "${op.tool}"`);
    }
  }
}

/**
 * Smart-sync decision for the batch loop: a *queue-only* op needs a
 * `context.sync()` after it only when it's the last op or the next op is
 * self-syncing (whose internal sync would otherwise flush these queued
 * mutations late). Self-syncing ops never need a trailing sync.
 */
export function shouldSyncAfterOp(
  index: number,
  operations: readonly BatchOperation[],
  selfSyncingTools: ReadonlySet<string>
): boolean {
  const op = operations[index];
  if (!op || selfSyncingTools.has(op.tool)) {
    return false;
  }
  const next = operations[index + 1];
  return !next || selfSyncingTools.has(next.tool);
}

/** Human-readable summary line for a completed batch. */
export function summarizeBatch(
  results: readonly BatchOperationResult[]
): string {
  const succeeded = results.filter((r) => r.status === "success").length;
  const failed = results.filter((r) => r.status === "error").length;
  return failed === 0
    ? `All ${succeeded} operations completed successfully.`
    : `${succeeded} succeeded, ${failed} failed out of ${results.length} operations.`;
}
